'use client';

/**
 * HubSpotCtaLoader — client leaf for HubSpot CTA cards (`hbspt.cta.load`).
 *
 * Reserved for future use (support/* and decoding/* archetypes). No confirmed
 * page in the ExternalEmbedShell §3.1 inventory consumes this branch.
 *
 * Source HTML for a HubSpot CTA renders an `hs-cta-wrapper` containing an
 * `hs-cta-node` placeholder and loads `https://js.hscta.net/cta/current.js`,
 * then calls `hbspt.cta.load(portalId, ctaId, opts)`.
 */

import { useEffect, useId, useRef } from 'react';

let ctaJsPromise: Promise<void> | null = null;

interface HbsptCta {
  load: (portalId: string | number, ctaId: string, opts?: Record<string, unknown>) => void;
}

// `Window.hbspt` is declared globally by src/components/hubspot/HubSpotForm.tsx
// as `{ forms: HbsptForms }`. We add the `cta` branch via a local cast at the
// call site rather than redeclaring the global (which would conflict).

function loadHubSpotCtaJs(): Promise<void> {
  if (typeof window === 'undefined') return Promise.resolve();
  if (ctaJsPromise) return ctaJsPromise;

  ctaJsPromise = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(
      'script[src*="js.hscta.net/cta/current.js"]',
    );
    if (existing) {
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', () => reject(new Error('CTA js failed')), { once: true });
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://js.hscta.net/cta/current.js';
    script.async = true;
    script.charset = 'utf-8';
    script.onload = () => resolve();
    script.onerror = () => {
      ctaJsPromise = null;
      reject(new Error('CTA js failed'));
    };
    document.head.appendChild(script);
  });

  return ctaJsPromise;
}

interface HubSpotCtaLoaderProps {
  portalId: string;
  ctaId: string;
  className?: string;
}

export function HubSpotCtaLoader({ portalId, ctaId, className }: HubSpotCtaLoaderProps) {
  const ref = useRef<HTMLSpanElement>(null);
  const instanceId = useId().replace(/:/g, '');

  useEffect(() => {
    loadHubSpotCtaJs()
      .then(() => {
        const hbspt = (window as unknown as { hbspt?: { cta?: HbsptCta } }).hbspt;
        hbspt?.cta?.load(portalId, ctaId, { useNewLoader: 'true' });
      })
      .catch(() => {
        // Non-fatal.
      });
  }, [portalId, ctaId]);

  return (
    <span
      ref={ref}
      className={`hs-cta-wrapper ${className ?? ''}`.trim()}
      id={`hs-cta-wrapper-${ctaId}-${instanceId}`}
    >
      <span className={`hs-cta-node hs-cta-${ctaId}`} id={`hs-cta-${ctaId}-${instanceId}`} />
    </span>
  );
}
